/**
 * Typed wrapper around `fetch` used by the frontend modules.
 */

import type { ApiError } from "./types";

/** Name of the cookie holding the CSRF token issued by the server. */
export const CSRF_COOKIE_NAME = "techcraft_csrf";

/** Header carrying the CSRF token on the state changing requests. */
export const CSRF_HEADER_NAME = "X-CSRF-Token";

export class HttpError extends Error {
    readonly status: number;

    constructor(status: number, message: string) {
        super(message);

        this.name = "HttpError";
        this.status = status;
    }
}

/** Return whether `value` looks like the error envelope of the API. */
export function isApiError(value: unknown): value is ApiError {
    return typeof value === "object" && value !== null && typeof (value as ApiError).detail === "string";
}

/** Perform a GET request and return the parsed JSON payload. */
export async function getJson<T>(url: string, signal?: AbortSignal): Promise<T> {
    const response = await fetch(url, {
        method: "GET",
        headers: { Accept: "application/json" },
        credentials: "same-origin",
        ...(signal ? { signal } : {}),
    });

    const payload: unknown = await response.json().catch(() => null);

    if (!response.ok) {
        throw new HttpError(response.status, isApiError(payload) ? payload.detail : `Chyba ${response.status}`);
    }

    return payload as T;
}

/** Return the value of the cookie `name`, or `null` when it is not set. */
export function readCookie(name: string): string | null {
    for (const part of document.cookie.split(";")) {
        const separator = part.indexOf("=");
        if (separator < 0) {
            continue;
        }

        if (part.slice(0, separator).trim() !== name) {
            continue;
        }

        return decodeURIComponent(part.slice(separator + 1).trim());
    }

    return null;
}

/** Perform a POST request with a JSON body and return the parsed JSON payload. */
export async function postJson<T>(url: string, body: unknown, signal?: AbortSignal): Promise<T> {
    const headers: Record<string, string> = {
        Accept: "application/json",
        "Content-Type": "application/json",
    };

    const token = readCookie(CSRF_COOKIE_NAME);
    if (token) {
        headers[CSRF_HEADER_NAME] = token;
    }

    const response = await fetch(url, {
        method: "POST",
        headers,
        credentials: "same-origin",
        body: JSON.stringify(body),
        ...(signal ? { signal } : {}),
    });

    const payload: unknown = await response.json().catch(() => null);

    if (!response.ok) {
        throw new HttpError(response.status, isApiError(payload) ? payload.detail : `Chyba ${response.status}`);
    }

    return payload as T;
}

/** Build an URL with the given query parameters, skipping empty values. */
export function withQuery(url: string, params: Readonly<Record<string, string | number | boolean | undefined>>): string {
    const search = new URLSearchParams();

    for (const [key, value] of Object.entries(params)) {
        if (value === undefined || value === "") {
            continue;
        }

        search.set(key, String(value));
    }

    const query = search.toString();

    return query ? `${url}?${query}` : url;
}

/**
 * Perform a multipart POST and return the parsed JSON payload.
 *
 * Used where a file travels with the request - pasting a picture into the
 * editor. `Content-Type` is deliberately left unset so that the browser writes
 * the multipart boundary itself; setting it by hand produces a body the server
 * cannot take apart.
 */
export async function postForm<T>(url: string, body: FormData, signal?: AbortSignal): Promise<T> {
    const headers: Record<string, string> = { Accept: "application/json" };

    const token = readCookie(CSRF_COOKIE_NAME);
    if (token) {
        headers[CSRF_HEADER_NAME] = token;
    }

    const response = await fetch(url, {
        method: "POST",
        headers,
        credentials: "same-origin",
        body,
        ...(signal ? { signal } : {}),
    });

    const payload: unknown = await response.json().catch(() => null);

    if (!response.ok) {
        throw new HttpError(response.status, isApiError(payload) ? payload.detail : `Chyba ${response.status}`);
    }

    return payload as T;
}
